import { useState, useCallback } from 'react';
import tokenService from 'services/token.service';

interface ValidationResult {
  valid: boolean;
  user?: {
    address: string;
    id: string;
    createdAt: string;
  };
  error?: string;
}

export const useTokenValidation = () => {
  const [isValidating, setIsValidating] = useState(false);

  const validateToken = useCallback(async (token: string): Promise<ValidationResult> => {
    if (!token) {
      return { valid: false, error: 'No token provided' };
    }

    setIsValidating(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_SECONDARY}/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ValidationResult = await response.json();

      // If token is invalid, clear it from storage
      if (!result.valid) {
        console.log('[useTokenValidation] Token is invalid:', result.error);
        tokenService.removeToken();
      }

      return result;
    } catch (error) {
      console.error('[useTokenValidation] Validation request failed:', error);
      return { valid: false, error: 'Network error' };
    } finally {
      setIsValidating(false);
    }
  }, []);

  const validateCurrentToken = useCallback(async (): Promise<boolean> => {
    const tokens = tokenService.tokens;

    if (!tokens?.accessToken) {
      console.log('[useTokenValidation] No token to validate');
      return false;
    }

    const result = await validateToken(tokens.accessToken);
    return result.valid;
  }, [validateToken]);

  return {
    validateToken,
    validateCurrentToken,
    isValidating,
  };
};
